import { get } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { updateOrCreateScheduleDaypartOverrides } from 'src/hooks';
import { createMissingScheduleDaypartsForLocationGroup } from 'src/hooks/api/schedules/scheduleDaypart/helper';
import type {
  ILocationGroup,
  ILocationGroupWeekdayDaypart,
  IScheduleDaypart,
  IScheduleDaypartOverride,
  IScheduleDaypartOverrideTransaction,
  IUntransformedScheduleDaypart,
  IViewLocationGroupList,
} from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

export const retainScheduleDaypartOverrides = async (
  locationCode: string,
  selectedLocationGroups: IViewLocationGroupList[],
  oldLocationGroups?: ILocationGroup[] | IViewLocationGroupList[],
) => {
  const hasPreviousLocationGroup =
    Array.isArray(oldLocationGroups) && oldLocationGroups.length > 0;
  if (hasPreviousLocationGroup) {
    for (const selectedGroup of selectedLocationGroups) {
      const previousLocationGroupID = getPreviousLocationGroupID(
        selectedGroup,
        oldLocationGroups,
      );
      if (previousLocationGroupID)
        await saveScheduleDaypartOverridesSnapshot(
          locationCode,
          previousLocationGroupID,
        );
    }
  }

  const locationCodeScheduleDaypartOverrides = await queryClient.fetchQuery({
    queryKey: queryKeys.scheduleDaypartOverrides.all([
      'pageSize=0',
      `location_code=${locationCode}`,
      'include=scheduleDayparts,locationGroupWeekdayDayparts,locationGroups',
    ]),
    queryFn: () => {
      const url = `/scheduleDaypartOverrides${generateQuery(
        ['scheduleDayparts', 'locationGroupWeekdayDayparts', 'locationGroups'],
        [`location_code=${locationCode}`, 'pageSize=0'],
      )}`;

      return requestCCApi({ url }) as Promise<{
        data: IScheduleDaypartOverride[];
      }>;
    },
  });

  const scheduleDaypartOverrides = locationCodeScheduleDaypartOverrides.data;
  const scheduleDayparts = get(
    locationCodeScheduleDaypartOverrides,
    'includes.scheduleDayparts.data',
    [],
  ) as IUntransformedScheduleDaypart[];
  const locationGroupWeekdayDayparts = get(
    locationCodeScheduleDaypartOverrides,
    'includes.scheduleDayparts.includes.locationGroupWeekdayDayparts.data',
    [],
  ) as ILocationGroupWeekdayDaypart[];
  const locationGroups = get(
    locationCodeScheduleDaypartOverrides,
    'includes.scheduleDayparts.includes.locationGroupWeekdayDayparts.includes.locationGroups.data',
    [],
  ) as ILocationGroup[];

  for (const selectedGroup of selectedLocationGroups) {
    await createMissingScheduleDaypartsForLocationGroup(selectedGroup.id);

    const selectedGroupScheduleDayparts = await queryClient.fetchQuery({
      queryKey: queryKeys.scheduleDayparts.all([
        'pageSize=0',
        `location_group_id=${selectedGroup.id}`,
      ]),
      queryFn: () => {
        const url = `/scheduleDayparts${generateQuery(
          [],
          ['pageSize=0', `location_group_id=${selectedGroup.id}`],
        )}`;

        return requestCCApi({ url }) as Promise<{
          data: IScheduleDaypart[];
        }>;
      },
    });

    const scheduleDaypartMapping: { [weekdayDaypartId: number]: number } = {};
    for (const scheduleDaypart of selectedGroupScheduleDayparts.data) {
      scheduleDaypartMapping[scheduleDaypart.weekday_daypart_id] =
        scheduleDaypart.id;
    }

    const matchingOverrides = scheduleDaypartOverrides.filter((override) => {
      const scheduleDaypart = scheduleDayparts.find(
        (daypart) => daypart.id === override.schedule_daypart_id,
      );
      if (!scheduleDaypart) return false;

      const associatedWeekdayDaypart = locationGroupWeekdayDayparts.find(
        (weekdayDaypart) =>
          weekdayDaypart.id ===
          scheduleDaypart.location_group_weekday_daypart_id,
      );
      if (!associatedWeekdayDaypart) return false;

      const associatedLocationGroup = locationGroups.find(
        (group) => group.id === associatedWeekdayDaypart.location_group_id,
      );
      if (!associatedLocationGroup) return false;

      return (
        associatedLocationGroup.channel_name === selectedGroup.channel_name
      );
    });

    const payload = [];
    for (const override of matchingOverrides) {
      const scheduleDaypart = scheduleDayparts.find(
        (daypart) => daypart.id === override.schedule_daypart_id,
      );
      if (!scheduleDaypart) continue;

      const associatedWeekdayDaypart = locationGroupWeekdayDayparts.find(
        (weekdayDaypart) =>
          weekdayDaypart.id ===
          scheduleDaypart.location_group_weekday_daypart_id,
      );
      if (!associatedWeekdayDaypart) continue;

      const scheduleDaypartId =
        scheduleDaypartMapping[associatedWeekdayDaypart.weekday_daypart_id];
      if (!scheduleDaypartId) continue;

      if (
        !checkIsOrphanedOverride(
          override,
          scheduleDayparts,
          locationGroupWeekdayDayparts,
          oldLocationGroups,
        )
      ) {
        continue; // skip preserving orphaned overrides
      }

      payload.push({
        resource: 'scheduleDaypartOverrides',
        method: 'PATCH',
        id: override.id,
        data: {
          schedule_daypart_id: scheduleDaypartId,
        },
      });
    }

    if (hasPreviousLocationGroup) {
      const previousLocationGroupID = getPreviousLocationGroupIDWithDefault(
        selectedGroup,
        oldLocationGroups,
      );

      const nullOverridePayload = previousLocationGroupID
        ? addMissingOverridesForPreviousGroup(
            matchingOverrides,
            scheduleDayparts,
            locationGroupWeekdayDayparts,
            scheduleDaypartMapping,
            selectedGroupScheduleDayparts.data,
            locationCode,
          )
        : [];

      if (nullOverridePayload.length > 0) {
        payload.push(...nullOverridePayload);
      }
    }

    if (payload.length > 0) {
      await updateOrCreateScheduleDaypartOverrides(payload);
      await queryClient.invalidateQueries(
        queryKeys.scheduleDaypartOverrides.invalidateAll(),
      );
    }
  }
};

export const checkIsOrphanedOverride = (
  override: IScheduleDaypartOverride,
  scheduleDayparts: IUntransformedScheduleDaypart[],
  locationGroupWeekdayDayparts: ILocationGroupWeekdayDaypart[],
  oldLocationGroups?: (ILocationGroup | IViewLocationGroupList)[],
): boolean => {
  const scheduleDaypart = scheduleDayparts.find(
    (daypart) => daypart.id === override.schedule_daypart_id,
  );
  if (!scheduleDaypart) return false;

  const associatedWeekdayDaypart = locationGroupWeekdayDayparts.find(
    (weekdayDaypart) =>
      weekdayDaypart.id === scheduleDaypart.location_group_weekday_daypart_id,
  );
  if (!associatedWeekdayDaypart) return false;

  const currentLocationGroupId = associatedWeekdayDaypart.location_group_id;

  return (
    oldLocationGroups?.some((group) => group.id === currentLocationGroupId) ??
    false
  );
};

const addMissingOverridesForPreviousGroup = (
  matchingOverrides: IScheduleDaypartOverride[],
  scheduleDayparts: IUntransformedScheduleDaypart[],
  weekdayDayparts: ILocationGroupWeekdayDaypart[],
  scheduleDaypartMapping: { [id: number]: number },
  selectedScheduleDayparts: IScheduleDaypart[],
  locationCode: string,
): IScheduleDaypartOverrideTransaction[] => {
  const payload: IScheduleDaypartOverrideTransaction[] = [];

  for (const newDaypart of selectedScheduleDayparts) {
    const alreadyPatched = matchingOverrides.some((override) => {
      const scheduleDaypart = scheduleDayparts.find(
        (d) => d.id === override.schedule_daypart_id,
      );
      const weekdayDaypart =
        scheduleDaypart &&
        weekdayDayparts.find(
          (d) => d.id === scheduleDaypart.location_group_weekday_daypart_id,
        );

      return (
        weekdayDaypart &&
        scheduleDaypartMapping[weekdayDaypart.weekday_daypart_id] ===
          newDaypart.id
      );
    });

    if (!alreadyPatched) {
      payload.push({
        resource: 'scheduleDaypartOverrides',
        method: 'POST',
        data: {
          schedule_daypart_id: newDaypart.id,
          location_code: locationCode,
          start_time: null,
        },
      });
    }
  }

  return payload;
};

export const getPreviousLocationGroupID = (
  newLocationGroup: IViewLocationGroupList,
  oldLocationGroups?: ILocationGroup[] | IViewLocationGroupList[],
) => {
  const prevLocationGroupId = oldLocationGroups?.find((locationGroup) => {
    return (
      !locationGroup.is_default &&
      locationGroup.channel_name === newLocationGroup.channel_name &&
      locationGroup.layout_type_id === newLocationGroup.layout_type_id &&
      locationGroup.id !== newLocationGroup.id
    );
  });

  return prevLocationGroupId?.id;
};

export const getPreviousLocationGroupIDWithDefault = (
  newLocationGroup: IViewLocationGroupList,
  oldLocationGroups?: ILocationGroup[] | IViewLocationGroupList[],
) => {
  const prevLocationGroupId = oldLocationGroups?.find((locationGroup) => {
    return (
      locationGroup.channel_name === newLocationGroup.channel_name &&
      locationGroup.layout_type_id === newLocationGroup.layout_type_id &&
      locationGroup.id !== newLocationGroup.id
    );
  });

  return prevLocationGroupId?.id;
};

export const saveScheduleDaypartOverridesSnapshot = async (
  locationCode: string,
  prevLocationGroupID: number,
) => {
  const currentGroupScheduleDayparts = await queryClient.fetchQuery({
    queryKey: queryKeys.scheduleDayparts.all([
      'pageSize=0',
      `location_group_id=${prevLocationGroupID}`,
    ]),
    queryFn: () => {
      const url = `/scheduleDayparts${generateQuery(
        [],
        ['pageSize=0', `location_group_id=${prevLocationGroupID}`],
      )}`;

      return requestCCApi({ url }) as Promise<{
        data: IScheduleDaypart[];
      }>;
    },
  });

  const locationCodeScheduleDaypartOverrides = await queryClient.fetchQuery({
    queryKey: queryKeys.scheduleDaypartOverrides.all([
      'pageSize=0',
      `location_code=${locationCode}`,
    ]),
    queryFn: () => {
      const url = `/scheduleDaypartOverrides${generateQuery(
        [],
        [`location_code=${locationCode}`, 'pageSize=0'],
      )}`;

      return requestCCApi({ url }) as Promise<{
        data: IScheduleDaypartOverride[];
      }>;
    },
  });

  const scheduleDaypartOverrides = locationCodeScheduleDaypartOverrides.data;

  const snapshot = createScheduleDaypartOverridesSnapshot(
    scheduleDaypartOverrides,
    currentGroupScheduleDayparts.data,
  );

  await syncOverridesFromSnapshot(
    snapshot,
    locationCode,
    scheduleDaypartOverrides,
  );
};

export const createScheduleDaypartOverridesSnapshot = (
  scheduleDaypartOverrides: IScheduleDaypartOverride[],
  currentGroupDayparts: IScheduleDaypart[],
): {
  id: number;
  start_time: string;
}[] => {
  return currentGroupDayparts.map((groupDaypart) => {
    const override = scheduleDaypartOverrides.find(
      (o) => o.schedule_daypart_id === groupDaypart.id,
    );

    return {
      id: groupDaypart.id,
      start_time: override
        ? override.start_time
        : groupDaypart.start_time ?? null,
    };
  });
};

export const syncOverridesFromSnapshot = async (
  snapshot: {
    id: number;
    start_time: string;
  }[],
  locationCode: string,
  existingOverrides: IScheduleDaypartOverride[],
) => {
  // Map to check if an override already exists for a specific schedule_daypart_id
  const existingOverrideMap = new Map<number, IScheduleDaypartOverride>();
  for (const override of existingOverrides) {
    existingOverrideMap.set(override.schedule_daypart_id, override);
  }
  const payload = snapshot
    .filter((entry) => {
      const overrideAlreadyExists = existingOverrideMap.has(entry.id);

      // Only create overrides only for the non-null values
      return !overrideAlreadyExists && (entry.start_time ?? null);
    })
    .map((entry) => ({
      resource: 'scheduleDaypartOverrides',
      method: 'POST',
      data: {
        schedule_daypart_id: get(entry, 'id'),
        location_code: locationCode,
        start_time: entry.start_time,
      },
    }));

  if (payload.length > 0) {
    await updateOrCreateScheduleDaypartOverrides(payload);
    queryClient.removeQueries(
      queryKeys.scheduleDaypartOverrides.invalidateAll(),
    );
  }
};
